import Alpine from "alpinejs";
import { getQueryParamValue } from "../../_utils/router";
import {
  findVariantByOptionValue,
  getValidSecondaryOptions,
  updateUrlWithVariantId,
  updateKlaviyoAddToCartTracking,
  calculateSellingPlanPrice,
} from "../../_utils/product";
import { addVariantsToCart } from "../../_api/cart";

/**
 * Initializes and returns an Alpine.js data component for a product detail page (PDP).
 * This component manages the product variants, active variant, and interactions like adding to cart.
 *
 * @param {Object} params - Configuration and initial data for the component.
 * @param {Array} params.variants - List of all product variants.
 * @param {string|number} params.initialActiveVariantId - Initial active variant ID.
 * @param {Object|null} params.variantFieldsToAdd - Additional fields to merge into each variant.
 * @param {Object|null} params.sellingPlanGroups - Selling plan data associated with variants.
 * @returns {Object} An Alpine.js data component.
 */
export const alpineDataPdpState = () =>
  Alpine.data(
    "pdpState",
    ({ variants, initialActiveVariantId, sellingPlanGroups = null }) => {
      const indexedVariants = variants.reduce((acc, variant) => {
        acc[variant.id] = variant;
        return acc;
      }, {});

      const indexedSellingPlansById = sellingPlanGroups?.reduce(
        (acc, group) => {
          group.selling_plans.forEach((plan) => {
            acc[plan.id] = plan;
          });
          return acc;
        },
        {}
      );

      const variantId = getQueryParamValue("variant") || initialActiveVariantId;
      const activeVariant =
        indexedVariants[variantId] || Object.values(indexedVariants)[0];

      const reactivePdpData = Alpine.reactive({ activeVariant });

      return {
        reactivePdpData,
        isSubscribing: "true",
        activeSellingPlanId: null,
        activeSellingPlanGroupId: null,
        hasPrimaryOption: false,
        validSecondaryOptions: [],
        indexedVariants,
        variants,
        sellingPlanGroups,
        indexedSellingPlansById,
        quantity: 1,
        isAddingToCart: false,
        isShowingSizeGuideDrawer: false,

        /**
         * Updates the active variant ID in the store and optionally updates the URL.
         * @param {number|string} variantId - The ID of the variant to set as active.
         */
        updateActiveVariantId(variantId) {
          if (variantId !== this.reactivePdpData.activeVariant.id) {
            const newActiveVariant = this.indexedVariants[variantId];
            if (newActiveVariant) {
              this.reactivePdpData.activeVariant = newActiveVariant;
              updateUrlWithVariantId(variantId);
            } else {
              console.error("Variant with ID:", variantId, "not found.");
            }
          }
        },

        /**
         * Updates the active selling plan and selling plan group.
         * @param {number|string} sellingPlanId - ID of the selling plan.
         * @param {number|string} sellingPlanGroupId - ID of the selling plan group.
         */
        updateSellingPlan(sellingPlanId, sellingPlanGroupId) {
          this.activeSellingPlanId = sellingPlanId;
          this.activeSellingPlanGroupId = sellingPlanGroupId;
        },

        /**
         * Gets the price for the active variant, applying selling plan discount if valid
         * @returns {number} The discounted price if a valid selling plan exists, otherwise the base price
         * @see calculateSellingPlanPrice
         * @example
         */
        getSellingPlanDiscount() {
          const basePrice = this.reactivePdpData.activeVariant.price;
          const { activeSellingPlanId } = this;

          if (!activeSellingPlanId) {
            return basePrice;
          }

          const sellingPlan = this.indexedSellingPlansById[activeSellingPlanId];
          const priceAdjustment = sellingPlan?.price_adjustments?.[0];

          const discountedPrice = calculateSellingPlanPrice({
            basePrice,
            priceAdjustment,
          });

          return discountedPrice ?? basePrice;
        },

        /**
         * Finds a variant by the option value.
         * @param {number} optionIndex - Index of the option in the options array.
         * @param {string} value - Value to search for among variant options.
         * @returns {Object|undefined} The found variant or undefined if not found.
         */
        findVariantbyOptionValue(optionIndex, value) {
          return findVariantByOptionValue(
            this.variants,
            this.reactivePdpData.activeVariant.options,
            optionIndex,
            value
          );
        },

        /**
         * Retrieves valid secondary options based on the selected primary option.
         * @param {number} optionIndex - Index of the primary option in the variant's options array.
         * @param {string|number} value - Value of the primary option selected.
         * @returns {Array} List of valid secondary options.
         */
        getValidSecondaryOptions(optionIndex, value) {
          return getValidSecondaryOptions(this.variants, optionIndex, value);
        },

        /**
         * Initializes valid secondary options if the primary option value is selected.
         * @param {number} optionIndex - Index of the option in the options array.
         * @param {string|number} value - Selected value of the option.
         */
        initializeValidSecondaryOptions(optionIndex, value) {
          this.hasPrimaryOption = true;
          const isOptionValueSelected = this.isOptionValueActive(
            optionIndex,
            value
          );
          if (isOptionValueSelected) {
            this.validSecondaryOptions = this.getValidSecondaryOptions(
              optionIndex,
              value
            );
          }
        },

        initializeIsSubscribing(isOnetimeDefault) {
          if (this.sellingPlanGroups.length === 0) {
            this.isSubscribing = "false";
            return;
          }
          this.isSubscribing = JSON.stringify(!isOnetimeDefault);
        },

        isValidSecondaryOption(value) {
          if (!this.hasPrimaryOption) return true;
          return this.validSecondaryOptions.includes(value);
        },

        /**
         * Handles changes to the primary option by finding and setting a new active variant.
         * Updates the list of valid secondary options based on the selected primary option.
         *
         * @param {number} optionIndex - Index of the primary option in the variant's options array.
         * @param {string|number} value - New value for the primary option.
         */
        handleChangePrimaryOption(optionIndex, value) {
          const variant = this.findVariantbyOptionValue(optionIndex, value);
          const validSecondaryOptions = this.getValidSecondaryOptions(
            optionIndex,
            value
          );
          if (variant) {
            this.updateActiveVariantId(variant.id);
            this.validSecondaryOptions = validSecondaryOptions;
          } else {
            console.error("Could not find variant with option:", value);
          }
        },

        /**
         * Updates the active variant based on changes to any option, typically triggered by user interaction.
         * This function is often used in response to direct selections of variant options on a product detail page.
         *
         * @param {number} optionIndex - Index of the option that is being changed.
         * @param {string|number} value - The newly selected option value.
         */
        handleChangeActiveOption(optionIndex, value) {
          const variant = this.findVariantbyOptionValue(optionIndex, value);
          if (variant) {
            this.updateActiveVariantId(variant.id);
          } else {
            console.error("Could not find variant with option:", value);
          }
        },

        isOptionValueActive(optionIndex, value) {
          return (
            this.reactivePdpData.activeVariant?.options[optionIndex] === value
          );
        },

        isOptionValueAvailable(optionIndex, value) {
          const variant = this.findVariantbyOptionValue(optionIndex, value);
          return variant?.available ?? false;
        },

        incrementQuantity() {
          const { inventory_policy, inventory_quantity } =
            this.reactivePdpData.activeVariant;
          const sellPastAvailable = inventory_policy == "continue";
          if (!sellPastAvailable && this.quantity >= inventory_quantity) return;
          this.quantity++;
        },

        incrementDisabled() {
          const { inventory_policy, inventory_quantity } =
            this.reactivePdpData.activeVariant;
          const sellPastAvailable = inventory_policy == "continue";
          return !sellPastAvailable && this.quantity >= inventory_quantity;
        },

        decrementQuantity() {
          if (this.quantity > 1) {
            this.quantity--;
          }
        },

        /**
         * Adds the active variant to the cart with specified options.
         * @param {Object} options - Options including properties and quantity to add to the cart.
         */
        async addActiveVariantToCart(options = {}) {
          if (!this.reactivePdpData.activeVariant) {
            console.error("No active variant available to add to cart.");
            return;
          }

          const item = {
            id: this.reactivePdpData.activeVariant.id,
            quantity: this.quantity,
            properties: options.properties || {},
          };

          if (this.isSubscribing === "true" && this.activeSellingPlanId) {
            item.selling_plan = this.activeSellingPlanId;
          }

          try {
            this.isAddingToCart = true;
            const response = await addVariantsToCart([item]);
            if (!response.ok) {
              throw new Error(
                `Failed to add item to cart: ${response.status} ${response.statusText}`
              );
            }

            if (Alpine.store("megaMenu").cartType === "custom") {
              await Alpine.store("cartState").fetchCart();
              Alpine.store("megaMenu").openCartDrawer(true);
              const currentCart = Alpine.store("cartState").cart;
              updateKlaviyoAddToCartTracking(currentCart);
            }
          } catch (error) {
            console.error("Failed to add active variant to cart:", error);
            throw error;
          } finally {
            this.isAddingToCart = false;
          }
        },

        checkShowAtc() {
          if (this.reactivePdpData.activeVariant.available) return true;
          const show =
            !this.reactivePdpData.activeVariant.available &&
            this.reactivePdpData.activeVariant.hide_back_in_stock_form;
          return show;
        },

        handleOpenSizeGuideDrawer() {
          document.documentElement.classList.add("overflow-hidden");
          this.isShowingSizeGuideDrawer = true;
        },

        handleCloseSizeGuideDrawer() {
          document.documentElement.classList.remove("overflow-hidden");
          this.isShowingSizeGuideDrawer = false;
        },
      };
    }
  );
